import * as Sentry from '@sentry/angular';
import {
  ApplicationConfig,
  provideZoneChangeDetection,
  importProvidersFrom,
  ErrorHandler,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter, withRouterConfig, Router } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import PrimengPreset from '@app/primeng.preset';
import { primeNg_strings } from '@shared/strings/common.strings';

import { environment } from '@env/environment';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@app/interceptors/auth.interceptor';
import { openReplayInterceptor } from '@app/interceptors/open-replay.interceptor';
import { MessageService } from 'primeng/api';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { InMemHeroService } from '@services/in-memory-data/in-memory-data.service';
import { provideAuth, LogLevel } from 'angular-auth-oidc-client';
import { app_main_routing_menu } from '@app/shared/app-main-routing-menu';
// import { CacheService } from '@services/cache.service';

export const appConfig: ApplicationConfig = {
  providers: [
    // Clearing cache at page load is not needed for now, left here for future reference
    // provideAppInitializer(() => inject(CacheService).clearCacheForApp()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        // https://angular.dev/api/router/RouterConfigOptions#api
        canceledNavigationResolution: 'computed',
      }),
    ), // PrimeNG depends on provideAnimationsAsync,
    // remove when a newer version has a fix
    provideAnimationsAsync(),

    provideAuth({
      config: {
        configId: environment.accessIdentity.configId,
        authority: environment.accessIdentity.authority,
        redirectUrl: `${window.location.origin}/${app_main_routing_menu.signin.path}`,
        postLogoutRedirectUri: `${window.location.origin}/${app_main_routing_menu.login.path}`,
        clientId: environment.accessIdentity.clientId,
        scope: 'openid accessIdentity offline_access',
        responseType: 'code',
        logLevel: environment.production ? LogLevel.None : LogLevel.Debug,
        autoUserInfo: false,
        silentRenew: false,
        historyCleanupOff: false,
        startCheckSession: false,
      },
    }),
    provideHttpClient(
      withInterceptors([authInterceptor, openReplayInterceptor]),
    ), // In-Memory Web API (development only)
    ...(environment.useInMemoryApi
      ? [
          importProvidersFrom(
            HttpClientInMemoryWebApiModule.forRoot(InMemHeroService, {
              apiBase: environment.apiMockUrl, // Base URL for the in-memory database
              dataEncapsulation: false, // Remove the 'data' wrapper
              delay: 600, // Simulate network delay
              passThruUnknownUrl: true, // Pass through requests that don't match the in-memory database
            }),
          ),
        ]
      : []),
    providePrimeNG({
      theme: {
        preset: PrimengPreset,
        options: {
          darkModeSelector: '.' + primeNg_strings.dark_mode_class_name,
          prefix: 'primeng',
        },
      },
      // ripple: true,
      // zIndex: {
      //   modal: 1100,    // dialog, sidebar
      //   overlay: 1000,  // dropdown, overlay panel
      //   menu: 1000,     // overlay menus
      //   tooltip: 1100   // tooltip
      // },
      // inputVariant: 'filled',
    }),
    MessageService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
